import React from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Box, Chip, Avatar, Typography, IconButton, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'date-fns';
import { alpha } from '@mui/material/styles';

const actorPalette = {
	user: 'success',
	system: 'info',
	unknown: 'warning'
};

const eventPalette = action => {
	const value = String(action || '').toLowerCase();
	if (value.startsWith('db-')) return 'primary';
	if (value.startsWith('system')) return 'secondary';
	if (value.startsWith('cron')) return 'warning';
	if (value.includes('login')) return 'success';
	return 'default';
};

export function AuditLogsTable({
	rows = [],
	meta = {},
	loading,
	sortBy,
	sortOrder,
	onPageChange,
	onPageSizeChange,
	onSortModelChange,
	onRowClick
}) {
	const paginationModel = React.useMemo(
		() => ({
			page: Math.max((meta?.page || 1) - 1, 0),
			pageSize: meta?.limit || 20
		}),
		[meta?.page, meta?.limit]
	);

	const sortModel = React.useMemo(() => {
		if (!sortBy) return [];
		return [{ field: sortBy, sort: sortOrder || 'desc' }];
	}, [sortBy, sortOrder]);

	const handlePaginationChange = model => {
		onPageChange?.(model.page + 1);
		onPageSizeChange?.(model.pageSize);
	};

	const handleSortChange = model => {
		const next = model?.[0];
		onSortModelChange?.({
			field: next?.field || '',
			sort: next?.sort || ''
		});
	};

	const columns = React.useMemo(
		() => [
			{
				field: 'timestamp',
				headerName: 'Timestamp',
				minWidth: 200,
				flex: 1.2,
				renderCell: params => {
					const date = params.value ? new Date(params.value) : null;
					const label = date && !Number.isNaN(date.getTime()) ? format(date, 'PPpp') : 'N/A';
					return (
						<Typography variant="body2" fontWeight={600}>
							{label}
						</Typography>
					);
				},
				sortable: true
			},
			{
				field: 'eventAction',
				headerName: 'Event',
				minWidth: 140,
				flex: 0.7,
				renderCell: params => (
					<Chip size="small" label={params.value || 'N/A'} color={eventPalette(params.value)} variant="outlined" />
				)
			},
			{
				field: 'operationType',
				headerName: 'Operation',
				minWidth: 120,
				flex: 0.6,
				renderCell: params => (
					<Chip
						size="small"
						label={(params.value || '').toUpperCase() || 'N/A'}
						color={params.value === 'delete' ? 'error' : 'default'}
						variant="outlined"
					/>
				)
			},
			{
				field: 'collection',
				headerName: 'Collection',
				minWidth: 140,
				flex: 0.8
			},
			{
				field: 'updatedBy',
				headerName: 'Updated By',
				minWidth: 200,
				flex: 1,
				renderCell: params => {
					const email = params.value || 'Unknown';
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<Avatar
								sx={{ width: 28, height: 28, bgcolor: 'primary.light', color: 'primary.contrastText', fontSize: 14 }}
							>
								{email.charAt(0).toUpperCase()}
							</Avatar>
							<Typography variant="body2" noWrap>
								{email}
							</Typography>
						</Box>
					);
				}
			},
			{
				field: 'actorType',
				headerName: 'Actor',
				minWidth: 120,
				flex: 0.6,
				renderCell: params => (
					<Chip size="small" label={params.value || 'unknown'} color={actorPalette[params.value] || 'default'} />
				)
			},
			{
				field: 'documentId',
				headerName: 'Document ID',
				minWidth: 200,
				flex: 1,
				renderCell: params => (
					<Tooltip title={params.value || 'N/A'}>
						<Typography
							variant="body2"
							noWrap
							sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}
						>
							{params.value || 'N/A'}
						</Typography>
					</Tooltip>
				)
			},
			{
				field: 'view',
				headerName: '',
				minWidth: 60,
				sortable: false,
				renderCell: params => (
					<Tooltip title="View details">
						<IconButton
							size="small"
							onClick={event => {
								event.stopPropagation();
								onRowClick?.(params.row);
							}}
						>
							<VisibilityIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				)
			}
		],
		[onRowClick]
	);

	return (
		<Box
			sx={{
				width: '100%',
				'& .MuiDataGrid-columnHeaders': { position: 'sticky', top: 0, zIndex: 1 }
			}}
		>
			<Box
				sx={{
					borderRadius: 3,
					border: theme => `1px solid ${theme.palette.divider}`,
					overflow: 'hidden',
					bgcolor: 'background.paper',
					height: { xs: 420, md: 520 },
					maxHeight: '70vh',
					minHeight: 360,
					display: 'flex',
					flexDirection: 'column'
				}}
			>
				<Box
					sx={{
						p: 1.5,
						borderBottom: theme => `1px solid ${theme.palette.divider}`,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						bgcolor: 'grey.50'
					}}
				>
					<Typography variant="subtitle2" color="text.secondary">
						{meta?.total || rows.length || 0} events • Page {meta?.page || paginationModel.page + 1} of {meta?.totalPages || '-'}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						Sorted by {sortBy || 'timestamp'} ({sortOrder || 'desc'})
					</Typography>
				</Box>
				<DataGrid
					rows={rows}
					getRowId={row => row._id || row.id}
					columns={columns}
					loading={loading}
					disableRowSelectionOnClick
					density="compact"
					paginationMode="server"
					sortingMode="server"
					rowCount={meta?.total || 0}
					paginationModel={paginationModel}
					onPaginationModelChange={handlePaginationChange}
					pageSizeOptions={[20, 50, 100, 200]}
					sortModel={sortModel}
					onSortModelChange={handleSortChange}
					onRowClick={params => onRowClick?.(params.row)}
					slots={{ toolbar: GridToolbar }}
					slotProps={{
						toolbar: {
							showQuickFilter: true,
							quickFilterProps: { debounceMs: 400 }
						}
					}}
					sx={{
						height: '100%',
						'& .MuiDataGrid-row:nth-of-type(odd)': theme => ({
							backgroundColor: alpha(theme.palette.primary.main, 0.015)
						}),
						'& .MuiDataGrid-cell': {
							alignItems: 'center'
						},
						'& .MuiDataGrid-cell:focus': { outline: 'none' },
						'& .MuiDataGrid-columnHeader:focus': { outline: 'none' }
					}}
				/>
			</Box>
		</Box>
	);
}
